/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import { Box, CircularProgress } from "@mui/material";
import DisplayAmount from "../../shared/DisplayAmount";
import { Button } from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import { Tooltip, IconButton } from "@mui/material";
import { AiOutlineFileSearch } from "react-icons/ai";
import { AiOutlineEdit } from "react-icons/ai";
import AgentAssignFeeGroupModal from "./AgentAssignFeeGroupModal";
import AgentFeeGroupDetails from "./AgentFeeGroupDetails";

ModuleRegistry.registerModules([AllCommunityModule]);

type RowData = {
  name: string;
  asset: string;
  method_name: string;
};

const AgentFeesTab: React.FC<{
  detail: any;
  setSwalProps: any;
  triggerReload: any;
  isFetchAssignedFeeGroups: boolean;
}> = ({ detail, setSwalProps, triggerReload, isFetchAssignedFeeGroups }) => {
  const [isOpenAssignFeeGroupModal, setIsOpenAssignFeeGroupModal] =
    useState<boolean>(false);
  const [isOpenFeeDetailsModal, setIsOpenFeeDetailsModal] =
    useState<boolean>(false);
  const [feeToViewDetails, setFeeToViewDetails] = useState<any>(null);

  const CustomLoadingOverlay = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <CircularProgress size={40} />
    </div>
  );

  const ActionsRenderer = (params: ICellRendererParams) => (
    <>
      <Tooltip title="View More Details" arrow placement="top">
        <div className="mr-5">
          <IconButton
            id={"button-detail-" + params.data?.id}
            onClick={() => handleClickDetail(params.data.id)}
            size="small"
          >
            <AiOutlineFileSearch className="h-5 w-5 text-navy-700" />
          </IconButton>
        </div>
      </Tooltip>
    </>
  );

  const columns: ColDef[] = [
    {
      field: "name",
      headerName: "Name",
      sortable: false,
      flex: 1,
    },
    {
      field: "asset",
      headerName: "Asset",
      sortable: false,
      flex: 1,
    },
    {
      field: "method_name",
      headerName: "Method",
      sortable: false,
      flex: 1,
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      width: 100,
      resizable: false,
      cellRenderer: ActionsRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
    },
  ];

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  const showAddAccountModal = () => {
    setIsOpenAssignFeeGroupModal(true);
  };

  const closeAddAccountModal = () => {
    setIsOpenAssignFeeGroupModal(false);
  };

  const handleClickDetail = (id: any) => {
    const fee = detail?.assigned_agent_feegroups.filter(
      (x: any) => x.id === id
    )[0];
    setFeeToViewDetails(fee);
    showFeeDetailsModal();
  };

  const showFeeDetailsModal = () => {
    setIsOpenFeeDetailsModal(true);
  };

  const closeFeeDetailsModal = () => {
    setFeeToViewDetails(null);
    setIsOpenFeeDetailsModal(false);
  };

  const triggerReloadListForAssignFeeeGroup = () => {
    triggerReload(detail.id);
  };

  const loading = !detail || isFetchAssignedFeeGroups;

  return (
    <>
      {isOpenAssignFeeGroupModal && detail && (
        <AgentAssignFeeGroupModal
          setSwalProps={setSwalProps}
          triggerReload={triggerReloadListForAssignFeeeGroup}
          agentDetail={detail}
          isOpen={isOpenAssignFeeGroupModal}
          onClose={closeAddAccountModal}
        />
      )}

      {isOpenFeeDetailsModal && feeToViewDetails && (
        <AgentFeeGroupDetails
          feeDetail={feeToViewDetails}
          isOpen={isOpenFeeDetailsModal}
          onClose={closeFeeDetailsModal}
        />
      )}

      <div className="px-4 py-8">
        <div className="mb-3 flex items-center justify-end">
          <Button
            isDisabled={!detail}
            onClick={() => showAddAccountModal()}
            className="btn btn-action !px-6"
          >
            <FaPlus />
            &nbsp;Assign Fee Group
          </Button>
        </div>
        <Box>
          <div style={{ width: "100%" }}>
            <AgGridReact
              theme={themeAlpine}
              rowData={loading ? [] : (detail?.assigned_agent_feegroups || [])}
              columnDefs={columns}
              defaultColDef={defaultColDef}
              getRowId={(params) => params.data.id}
              rowHeight={48}
              headerHeight={44}
              domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
              suppressPaginationPanel={true}
              loading={loading}
              loadingOverlayComponent={CustomLoadingOverlay}
              overlayNoRowsTemplate="No data available"
            />
          </div>
        </Box>
      </div>
    </>
  );
};

export default AgentFeesTab;
